import { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Loader2,
  Apple,
  Play,
  Star,
  Download,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Trophy,
  Target,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import FAQSection from "../components/homepage/FAQSection";
import LeadCaptureModal from "../components/shared/LeadCaptureModal";
import { generateAudit } from "../lib/auditMock";

const STEPS = [
  "Fetching App Store listing…",
  "Analyzing metadata & keywords…",
  "Scoring screenshot conversion…",
  "Pulling competitor benchmarks…",
  "Compiling audit report…",
];

const SAMPLE_APPS = [
  {
    name: "Spotify",
    fullName: "Spotify - Music and Podcasts",
    category: "Music · Free",
    rating: 4.8,
    initial: "S",
    color: "bg-emerald-500",
  },
  {
    name: "Instagram",
    fullName: "Instagram",
    category: "Social · Free",
    rating: 4.7,
    initial: "Ig",
    color: "bg-pink-500",
  },
  {
    name: "Duolingo",
    fullName: "Duolingo: Language Lessons",
    category: "Education · Freemium",
    rating: 4.7,
    initial: "D",
    color: "bg-emerald-600",
  },
  {
    name: "Notion",
    fullName: "Notion - notes, docs, tasks",
    category: "Productivity · Freemium",
    rating: 4.6,
    initial: "N",
    color: "bg-zinc-900",
  },
  {
    name: "Swiggy",
    fullName: "Swiggy Food & Grocery Order",
    category: "Food Delivery · India",
    rating: 4.5,
    initial: "Sw",
    color: "bg-orange-500",
  },
  {
    name: "Paytm",
    fullName: "Paytm: Secure UPI Payments",
    category: "FinTech · India",
    rating: 4.6,
    initial: "P",
    color: "bg-electric",
  },
];

function ScoreBar({ value, label }) {
  const color = value >= 70 ? "bg-emerald-500" : value >= 50 ? "bg-electric" : "bg-orange-500";
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="text-zinc-600 font-medium">{label}</span>
        <span className="font-display font-bold text-charcoal">{value}/100</span>
      </div>
      <div className="h-1.5 bg-zinc-200 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${value}%` }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className={`h-full ${color}`}
        />
      </div>
    </div>
  );
}

function LeadModalLegacy() {
  return null;
}

export default function FreeAsoAuditPage() {
  const [query, setQuery] = useState("");
  const [scanning, setScanning] = useState(false);
  const [step, setStep] = useState(0);
  const [audit, setAudit] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const resultsRef = useRef(null);
  const pickerRef = useRef(null);

  // Close picker on outside click
  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pickerOpen]);

  const runAudit = async (forced) => {
    const q = (typeof forced === "string" ? forced : query).trim();
    if (!q) {
      toast.error("Paste an App Store URL or app name.");
      return;
    }
    setQuery(q);
    setAudit(null);
    setScanning(true);
    setStep(0);

    // Animated multi-step loader (~5s total)
    for (let i = 0; i < STEPS.length; i++) {
      await new Promise((r) => setTimeout(r, 900));
      setStep(i + 1);
    }
    const result = generateAudit(q);
    setAudit(result);
    setScanning(false);
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const onSubmit = (e) => {
    e?.preventDefault();
    runAudit();
  };

  const pickApp = (app) => {
    setPickerOpen(false);
    runAudit(app.fullName);
  };

  return (
    <>
      <Helmet>
        <title>
          Get Free ASO Audit — Instant App Store Optimization Report | Lumotis
        </title>
        <meta
          name="description"
          content="Get an instant, free 12-page ASO audit for your iOS or Android app. Paste your App Store URL — we'll scan keywords, screenshots, ratings and conversion in 60 seconds."
        />
        <link rel="canonical" href="https://lumotis.com/free-aso-audit" />
      </Helmet>
      <Navbar />

      <main data-testid="free-audit-page">
        {/* HERO + SEARCH */}
        <section className="relative pt-28 md:pt-32 pb-12 md:pb-16 bg-white">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute inset-0 bg-grid-pattern opacity-70" />
            <div className="absolute right-0 top-0 w-2/3 h-2/3 bg-gradient-to-bl from-electric/10 via-transparent to-transparent" />
          </div>

          <div className="container relative max-w-4xl">
            <p className="text-overline text-electric mb-4">Free ASO Audit</p>
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-display text-[2.4rem] sm:text-5xl lg:text-[4rem] leading-[1.04] font-extrabold text-charcoal text-balance"
              data-testid="audit-headline"
            >
              Drop your app's link.{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Get a free ASO audit</span>
                <span className="absolute inset-x-0 bottom-1 h-3 bg-electric/30 -z-0" />
              </span>{" "}
              in 60 seconds.
            </motion.h1>
            <p className="mt-5 text-lg md:text-xl text-zinc-600 max-w-2xl leading-relaxed">
              Paste your App Store or Google Play URL — or just type your app
              name. We'll scan your listing, keywords, screenshots, ratings and
              conversion gaps and deliver a 12-page audit to your email +
              WhatsApp.
            </p>

            {/* Search */}
            <form
              onSubmit={onSubmit}
              className="mt-9 relative"
              data-testid="audit-search-form"
            >
              <div
                ref={pickerRef}
                className="bg-white border border-charcoal shadow-xl rounded-xl flex items-stretch"
              >
                <span className="grid place-items-center pl-5 pr-2 text-zinc-400">
                  <Search className="w-5 h-5" />
                </span>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => !scanning && setPickerOpen(true)}
                  onClick={() => !scanning && setPickerOpen(true)}
                  placeholder="Paste App Store URL or type app name (e.g. Spotify, Duolingo)…"
                  disabled={scanning}
                  data-testid="audit-search-input"
                  aria-label="App Store URL or app name"
                  aria-haspopup="listbox"
                  aria-expanded={pickerOpen}
                  className="flex-1 px-2 py-4 text-sm md:text-base outline-none disabled:bg-zinc-50 min-w-0"
                />
                <button
                  type="submit"
                  disabled={scanning}
                  data-testid="audit-search-submit"
                  aria-label="Run free ASO audit"
                  className="bg-electric text-white px-5 md:px-7 font-semibold hover:bg-electric-400 transition-colors inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {scanning ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="hidden sm:inline">Scanning…</span>
                    </>
                  ) : (
                    <>
                      Run Audit
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>

              {/* Sample apps dropdown — appears below search input on click/focus */}
              <AnimatePresence>
                {pickerOpen && !scanning && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.18 }}
                    role="listbox"
                    data-testid="audit-sample-panel"
                    className="absolute left-0 right-0 top-full mt-2 bg-white border border-charcoal shadow-xl shadow-electric/20 rounded-xl z-50"
                  >
                    <p className="text-overline text-zinc-500 px-5 pt-4 pb-2">
                      Try a sample app
                    </p>
                    <ul className="max-h-[26rem] overflow-y-auto">
                      {SAMPLE_APPS.map((app) => (
                        <li key={app.name}>
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              pickApp(app);
                            }}
                            data-testid={`audit-sample-${app.name.toLowerCase()}`}
                            className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-zinc-50 text-left border-t border-zinc-100 transition-colors"
                            role="option"
                            aria-selected={false}
                          >
                            <span
                              className={`w-11 h-11 ${app.color} text-white grid place-items-center font-display font-extrabold text-base flex-shrink-0`}
                            >
                              {app.initial}
                            </span>
                            <span className="flex-1 min-w-0">
                              <span className="block text-sm font-semibold text-charcoal truncate">
                                {app.fullName}
                              </span>
                              <span className="block text-xs text-zinc-500 mt-0.5">
                                {app.category}
                              </span>
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs text-electric font-semibold">
                              <Star className="w-3 h-3 fill-electric" />
                              {app.rating}
                            </span>
                          </button>
                        </li>
                      ))}
                      <li className="border-t border-zinc-100">
                        <p className="px-5 py-3 text-[11px] text-zinc-500 leading-relaxed">
                          Or paste your own App Store / Google Play URL above
                          and press Run Audit.
                        </p>
                      </li>
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </form>

            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-zinc-500">
              <span className="inline-flex items-center gap-1.5">
                <Apple className="w-3.5 h-3.5" /> iOS
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Play className="w-3.5 h-3.5" /> Android
              </span>
              <span>· No signup to scan</span>
              <span>· Email + WhatsApp delivery</span>
              <span>· Free for first 100 scans / day</span>
            </div>
          </div>
        </section>

        {/* LOADER */}
        <AnimatePresence>
          {scanning && (
            <motion.section
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="bg-zinc-50 border-y border-zinc-200 py-16"
              data-testid="audit-loader"
            >
              <div className="container max-w-2xl">
                <div className="flex items-center gap-3 mb-6">
                  <Loader2 className="w-5 h-5 text-electric animate-spin" />
                  <p className="text-overline text-electric">
                    Scanning your app listing
                  </p>
                </div>
                <ul className="space-y-3">
                  {STEPS.map((s, i) => {
                    const done = i < step;
                    const active = i === step;
                    return (
                      <li
                        key={s}
                        className={`flex items-center gap-3 text-sm font-medium ${done ? "text-charcoal" : active ? "text-charcoal" : "text-zinc-400"}`}
                      >
                        <span
                          className={`w-7 h-7 grid place-items-center border ${
                            done
                              ? "bg-electric border-electric text-white"
                              : active
                                ? "border-electric text-electric"
                                : "border-zinc-300 text-zinc-400"
                          }`}
                        >
                          {done ? (
                            <CheckCircle2 className="w-4 h-4" />
                          ) : active ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <span className="text-[11px] font-bold">{i + 1}</span>
                          )}
                        </span>
                        {s}
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-8 h-1.5 bg-zinc-200 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(step / STEPS.length) * 100}%` }}
                    transition={{ duration: 0.4 }}
                    className="h-full bg-electric"
                  />
                </div>
              </div>
            </motion.section>
          )}
        </AnimatePresence>

        {/* RESULTS */}
        <AnimatePresence>
          {audit && (
            <motion.section
              ref={resultsRef}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-zinc-50 border-y border-zinc-200 py-16 md:py-20"
              data-testid="audit-results"
            >
              <div className="container">
                {/* App overview */}
                <div className="grid lg:grid-cols-12 gap-6 mb-10">
                  <div className="lg:col-span-7 bg-charcoal text-white p-8 shadow-xl shadow-electric/20 rounded-2xl">
                    <div className="flex items-center justify-between mb-6">
                      <p className="text-overline text-electric">Audit complete</p>
                      <div className="flex items-center gap-2 text-xs text-zinc-400">
                        {audit.platform !== "android" && (
                          <Apple className="w-4 h-4" />
                        )}
                        {audit.platform !== "ios" && <Play className="w-4 h-4" />}
                      </div>
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl font-extrabold leading-tight">
                      {audit.appName}
                    </h2>
                    <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                          Rating
                        </p>
                        <p className="font-display text-3xl font-black mt-1 inline-flex items-center gap-1">
                          {audit.rating}
                          <Star className="w-5 h-5 fill-electric text-electric" />
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                          Reviews
                        </p>
                        <p className="font-display text-3xl font-black mt-1">
                          {audit.reviews.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                          Installs
                        </p>
                        <p className="font-display text-3xl font-black mt-1">
                          {audit.installs}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                          CVR (est.)
                        </p>
                        <p className="font-display text-3xl font-black mt-1">
                          {audit.cvr}%
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="lg:col-span-5 bg-white border border-zinc-200 p-8 flex flex-col">
                    <p className="text-overline text-zinc-500 mb-3">
                      Overall ASO Health
                    </p>
                    <div className="flex items-end gap-2">
                      <span
                        className="font-display text-7xl font-black text-charcoal leading-none"
                        data-testid="audit-overall-score"
                      >
                        {audit.overall}
                      </span>
                      <span className="text-2xl font-display font-bold text-zinc-400 mb-2">
                        / 100
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-zinc-600">
                      {audit.overall >= 70
                        ? "Solid foundation. Specific lifts available below."
                        : audit.overall >= 50
                          ? "Mid-pack. Significant rank + CVR upside available."
                          : "Major opportunity. Quick wins flagged below."}
                    </p>
                    <button
                      onClick={() => setModalOpen(true)}
                      data-testid="audit-download-cta"
                      className="mt-auto inline-flex items-center justify-center gap-2 bg-electric text-white px-5 py-3.5 font-semibold hover:bg-electric-400 transition-colors shadow-lg rounded-xl"
                    >
                      <Download className="w-4 h-4" /> Download Full Report
                    </button>
                  </div>
                </div>

                {/* Scores + keyword table */}
                <div className="grid lg:grid-cols-12 gap-6 mb-10">
                  <div className="lg:col-span-5 bg-white border border-zinc-200 p-8">
                    <div className="flex items-center gap-2 mb-6">
                      <Target className="w-5 h-5 text-electric" />
                      <p className="text-overline text-zinc-500">
                        Listing Scorecard
                      </p>
                    </div>
                    <div className="space-y-4">
                      {Object.entries(audit.scores).map(([k, v]) => (
                        <ScoreBar
                          key={k}
                          value={v}
                          label={k.replace(/^\w/, (c) => c.toUpperCase())}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="lg:col-span-7 bg-white border border-zinc-200 p-8">
                    <div className="flex items-center gap-2 mb-6">
                      <TrendingUp className="w-5 h-5 text-electric" />
                      <p className="text-overline text-zinc-500">
                        Top keywords (current rank)
                      </p>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                          <th className="py-2 font-medium">Keyword</th>
                          <th className="py-2 font-medium">Volume</th>
                          <th className="py-2 font-medium">Difficulty</th>
                          <th className="py-2 font-medium text-right">Rank</th>
                        </tr>
                      </thead>
                      <tbody>
                        {audit.keywords.map((kw) => (
                          <tr
                            key={kw.keyword}
                            className="border-b border-zinc-100 last:border-0"
                          >
                            <td className="py-3 font-medium text-charcoal">
                              {kw.keyword}
                            </td>
                            <td className="py-3 text-zinc-600">{kw.volume}</td>
                            <td className="py-3 text-zinc-600">
                              {kw.difficulty}
                            </td>
                            <td
                              className={`py-3 text-right font-display font-bold ${
                                kw.rank <= 10
                                  ? "text-emerald-600"
                                  : kw.rank <= 30
                                    ? "text-electric"
                                    : "text-orange-500"
                              }`}
                            >
                              #{kw.rank}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Gaps */}
                <div className="bg-white border border-zinc-200 p-8 mb-10">
                  <div className="flex items-center gap-2 mb-6">
                    <AlertTriangle className="w-5 h-5 text-electric" />
                    <p className="text-overline text-zinc-500">
                      Gaps & opportunities ({audit.gaps.length})
                    </p>
                  </div>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
                    {audit.gaps.map((g, idx) => (
                      <li
                        key={g.title}
                        className="bg-white p-5"
                        data-testid={`audit-gap-${idx}`}
                      >
                        <div className="flex items-start gap-3">
                          <span
                            className={`mt-1 w-2 h-2 flex-shrink-0 ${
                              g.severity === "high"
                                ? "bg-red-500"
                                : g.severity === "medium"
                                  ? "bg-orange-500"
                                  : "bg-electric"
                            }`}
                          />
                          <div>
                            <p className="font-display font-bold text-charcoal text-[15px] leading-snug">
                              {g.title}
                            </p>
                            <p className="mt-1 text-sm text-zinc-600 leading-relaxed">
                              {g.detail}
                            </p>
                            <span
                              className={`mt-2 inline-block text-[10px] uppercase tracking-widest font-bold ${
                                g.severity === "high"
                                  ? "text-red-600"
                                  : g.severity === "medium"
                                    ? "text-orange-600"
                                    : "text-electric"
                              }`}
                            >
                              {g.severity} priority
                            </span>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Competitors */}
                <div className="bg-charcoal text-white p-8">
                  <div className="flex items-center gap-2 mb-6">
                    <Trophy className="w-5 h-5 text-electric" />
                    <p className="text-overline text-electric">
                      Category leaderboard (estimated)
                    </p>
                  </div>
                  <ol className="space-y-3">
                    {audit.competitors.map((c, idx) => {
                      const isYou = c.name === audit.appName;
                      return (
                        <li
                          key={c.name}
                          className={`flex items-center justify-between gap-4 py-3 px-4 ${
                            isYou
                              ? "bg-electric/10 border border-electric"
                              : "border border-white/10"
                          }`}
                        >
                          <div className="flex items-center gap-4 min-w-0">
                            <span className="font-display text-2xl font-black text-electric w-8">
                              #{idx + 1}
                            </span>
                            <span
                              className={`font-display font-bold truncate ${isYou ? "text-white" : "text-zinc-200"}`}
                            >
                              {c.name} {isYou && "(your app)"}
                            </span>
                          </div>
                          <div className="flex items-center gap-6 text-sm">
                            <span className="inline-flex items-center gap-1 text-zinc-400">
                              <Star className="w-3.5 h-3.5 fill-electric text-electric" />
                              {c.rating}
                            </span>
                            <span className="font-display text-xl font-black text-white">
                              {c.score}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </div>

                {/* Bottom CTA */}
                <div className="text-center mt-12">
                  <p className="font-display text-2xl md:text-3xl font-bold text-charcoal text-balance">
                    Want the full 12-page report + a 15-min video walkthrough?
                  </p>
                  <button
                    onClick={() => setModalOpen(true)}
                    data-testid="audit-download-cta-bottom"
                    className="mt-6 inline-flex items-center gap-2 bg-electric text-white px-7 py-4 font-semibold hover:bg-electric-400 transition-colors shadow-xl rounded-xl"
                  >
                    <Download className="w-4 h-4" /> Download Full Report
                  </button>
                  <p className="mt-3 text-xs text-zinc-500">
                    Delivered to your email + WhatsApp in 2 minutes.
                  </p>
                </div>
              </div>
            </motion.section>
          )}
        </AnimatePresence>

        {/* Empty-state value strip */}
        {!audit && !scanning && (
          <section className="bg-zinc-50 border-y border-zinc-200 py-20" data-testid="audit-empty-state">
            <div className="container max-w-5xl">
              <p className="text-overline text-electric mb-3">
                What you'll get
              </p>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold text-charcoal text-balance">
                A 12-page audit, not a 12-second score.
              </h2>
              <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
                {[
                  {
                    icon: Target,
                    title: "Listing scorecard",
                    text: "Title, subtitle, keywords, description, screenshots — graded.",
                  },
                  {
                    icon: TrendingUp,
                    title: "Keyword universe",
                    text: "Your top 50 keywords with current rank, volume and difficulty.",
                  },
                  {
                    icon: Sparkles,
                    title: "Action plan",
                    text: "A prioritized 30/60/90-day playbook to lift rank + CVR.",
                  },
                ].map((b) => {
                  const Icon = b.icon;
                  return (
                    <div key={b.title} className="bg-white p-7">
                      <Icon className="w-6 h-6 text-electric mb-4" />
                      <h3 className="font-display text-lg font-bold text-charcoal">
                        {b.title}
                      </h3>
                      <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
                        {b.text}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        <FAQSection />
      </main>

      <Footer />
      <LeadCaptureModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        eyebrow="Free Audit Delivery"
        title="Where should we send your full ASO audit?"
        description="Get the 12-page PDF + a 15-min video walkthrough delivered to your email and WhatsApp within 2 minutes."
        ctaLabel="Send to my email & WhatsApp"
        successTitle="Audit en route!"
        successBody="Check your inbox in 2 minutes for the 12-page PDF. We'll WhatsApp the walkthrough video shortly after."
        source="free-aso-audit-download"
        extraPayload={{
          app_name: audit?.appName,
          app_store_url: query,
          platform: audit?.platform,
          audit_score: audit?.overall,
        }}
      />
    </>
  );
}
